import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocalize } from '@treams/treams-hooks';
import { Accordion, Paragraph, Pill, QuillText } from '@treams/treams-ui';

import { useCompetencyProgress } from '../hooks/useCompetencyProgress';
import { GrowthPathAppraisal, GrowthPathLevelOption } from '../types';
import CompetencyProgressPill from './CompetencyProgressPill';

type Props = {
	index: number;
	level: GrowthPathLevelOption;
	appraisal?: GrowthPathAppraisal;
	min: number;
};

const GrowthPathCompetencyLevel: FC<Props> = ({ level, index, appraisal, min }) => {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const progress = useCompetencyProgress(appraisal ?? null, min);

	const getHeaderRight = () => {
		// It needs to show a pill if it's the current level or the minimum level.
		if (index !== progress?.level && index !== min) {
			return undefined;
		}

		// If the level is on track, override desired level pill with progress pill
		if (progress?.progress === 'on-track') {
			return <CompetencyProgressPill progress={progress.progress} />;
		}

		// Show desired level pill for the minimum level
		if (min === index) {
			return (
				<Pill tint="purple" small flexShrink={0}>
					{t('growthPath.competency.desiredLevel')}
				</Pill>
			);
		}

		// Show progress pill for above/below level (only on achieved level)
		if (progress?.progress) {
			return <CompetencyProgressPill progress={progress.progress} />;
		}

		return undefined;
	};

	const translatedCriteria = level.criteria && localize(level.criteria).text;
	const hasCriteria = !!(translatedCriteria && translatedCriteria.ops.length > 0);

	return (
		<Accordion
			compact
			title={localize(level.level.title).text}
			headerRight={getHeaderRight()}
			borderless
		>
			<Paragraph small>
				{hasCriteria ? (
					<QuillText value={translatedCriteria} />
				) : (
					t('growthPath.competency.noCriteria')
				)}
			</Paragraph>
		</Accordion>
	);
};

export default GrowthPathCompetencyLevel;
